import React from 'react'
import { Button, Slider } from 'rsuite'
import Icon from './Icon'

/**
 * Round a value to a step and keep it within its range
 * @param {number} value value to round
 * @param {number} min lowest value
 * @param {number} max highest value
 * @param {number} step size of a step
 * @returns {number} rounded value
 */
const toStep = ( value, min, max, step ) =>
{
  const rounded = Math.round( value / step ) * step
  return Number( Math.min( max, Math.max( min, rounded ) ).toFixed( 2 ) )
}

/**
 * Choose a number with a slider, or a step at a time with the buttons beside it
 * @param {Object} props component props
 * @param {number} props.value chosen value
 * @param {number} props.min lowest value
 * @param {number} props.max highest value
 * @param {number} props.step size of a step
 * @param {string} props.label what the value is, for the names of the buttons
 * @param {Function} props.format turns the value into the text shown beside the slider
 * @param {Function|null} props.onChange called with the new value
 * @returns {JSX.Element} slider with buttons
 */
const StepSlider = ( { value = 0, min = 0, max = 1, step = 0.1, label = '', format = v => v, onChange = null } ) =>
{
  /**
   * Choose a value
   * @param {number} next new value
   * @returns {void}
   */
  const change = ( next ) =>
  {
    const stepped = toStep( next, min, max, step )
    if ( onChange && stepped !== value ) onChange( stepped )
  }

  return (
    <div className="step-slider">
      <Button
        aria-label={ `Decrease ${ label }` }
        title={ `Decrease ${ label }` }
        disabled={ value <= min }
        onClick={ () => change( value - step ) }
      ><Icon icon="minus"/></Button>
      <Slider
        className="step-slider-track"
        value={ value }
        min={ min }
        max={ max }
        step={ step }
        tooltip={ false }
        progress
        onChange={ change }
      />
      <Button
        aria-label={ `Increase ${ label }` }
        title={ `Increase ${ label }` }
        disabled={ value >= max }
        onClick={ () => change( value + step ) }
      ><Icon icon="plus"/></Button>
      <span className="step-slider-value">{ format( value ) }</span>
    </div>
  )
}

export default StepSlider
